import React, { useState } from 'react';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { Button, Popover, Switch, Tooltip } from 'antd';
import { useTranslate } from '@/translations';
import { Icon } from '@/ui/components';
import type { DesktopNotificationChannel } from './desktop';
import type { AppNotificationSettings } from './model';

interface NotificationSettingsPanelProps {
  settings: AppNotificationSettings;
  desktopChannel: DesktopNotificationChannel;
  onChange(settings: AppNotificationSettings): void;
}

export const NotificationSettingsPanel = ({
  settings,
  desktopChannel,
  onChange,
}: NotificationSettingsPanelProps) => {
  const { t } = useTranslate();
  const [permissionDenied, setPermissionDenied] = useState(false);
  const desktopGranted = desktopChannel.permission() === 'granted';

  const setDesktopEnabled = async (enabled: boolean) => {
    if (!enabled) {
      setPermissionDenied(false);
      onChange({
        ...settings,
        desktopEnabled: false,
      });
      return;
    }
    const permission = desktopGranted
      ? 'granted'
      : await desktopChannel.requestPermission();
    if (permission !== 'granted') {
      setPermissionDenied(true);
      onChange({
        ...settings,
        desktopEnabled: false,
      });
      return;
    }
    setPermissionDenied(false);
    onChange({ ...settings, desktopEnabled: true });
  };

  const content = (
    <div className="notification-settings-panel">
      <label className="notification-settings-panel__row">
        <span>{t('notificationDesktop')}</span>
        <Switch
          aria-label={t('notificationDesktop')}
          checked={settings.desktopEnabled}
          onChange={(enabled) => void setDesktopEnabled(enabled)}
        />
      </label>
      <label className="notification-settings-panel__row">
        <span>{t('notificationPreviews')}</span>
        <Switch
          aria-label={t('notificationPreviews')}
          checked={settings.showPreviews}
          disabled={!settings.desktopEnabled || !desktopGranted}
          onChange={(showPreviews) => onChange({ ...settings, showPreviews })}
        />
      </label>
      <label className="notification-settings-panel__row">
        <span>{t('notificationSounds')}</span>
        <Switch
          aria-label={t('notificationSounds')}
          checked={settings.soundsEnabled}
          onChange={(soundsEnabled) => onChange({ ...settings, soundsEnabled })}
        />
      </label>
      {permissionDenied && (
        <p className="notification-settings-panel__recovery" role="alert">
          {t('notificationPermissionDenied')}
        </p>
      )}
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="bottomRight">
      <Tooltip title={t('notificationSettings')}>
        <Button
          type="text"
          size="small"
          aria-label={t('notificationSettings')}
          icon={<Icon icon={faGear} />}
        />
      </Tooltip>
    </Popover>
  );
};
